"use client";

import { useEffect, useState } from "react";

import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";

import jwt_decode from "jwt-decode";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { ACCESS_LEVEL } from "../../constant/constant";
import { useSelector } from "react-redux";

import CreateTipDrawer from "./tipForm.js";
import TipViewDialog from "./tipViewDialog.js";

export default function TipManager() {

  const [tips, setTips]               = useState([]);
  const [totalTipAmount, setTotalTipAmount] = useState(0);
  const [selectedTip, setSelectedTip] = useState(null);
  const [openView, setOpenView]       = useState(false);
  const [openDrawer, setOpenDrawer]   = useState(false);
  const [page, setPage]               = useState(0);
  const rowsPerPage = 20;
  const [pagination, setPagination]   = useState({});
  const [dateFrom, setDateFrom]       = useState("");
  const [dateTo, setDateTo]           = useState("");
  const [empFilter, setEmpFilter]     = useState("");
  const [markPaidTip, setMarkPaidTip] = useState(null);
  const [paidRemark, setPaidRemark]   = useState("");
  const [paidLoading, setPaidLoading] = useState(false);

  const token = useSelector((state) => state.loginSlice.token);
  let isCashier = false;
  try { isCashier = token ? jwt_decode(token)?.accessLevel === ACCESS_LEVEL.CASHIER : false; } catch { isCashier = false; }

  const fetchTips = async () => {
    try {
      let url = `${apiBaseUrl}/tip?page=${page + 1}&size=${rowsPerPage}`;
      if (dateFrom)   url += `&date_from=${dateFrom}`;
      if (dateTo)     url += `&date_to=${dateTo}`;
      if (empFilter)  url += `&employee_name=${encodeURIComponent(empFilter)}`;

      const res = await axiosRequest("GET", url, token);
      setTips(res?.data?.tipList || []);
      setTotalTipAmount(res?.data?.total_tip_amount || 0);
      setPagination(res?.data?.paginationData || {});
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchTips();
  }, [page, rowsPerPage, dateFrom, dateTo, empFilter]);

  const handleMarkPaidConfirm = async () => {
    setPaidLoading(true);
    try {
      await axiosRequest("PUT", `${apiBaseUrl}/tip`, token, {
        id: markPaidTip._id,
        status: "PAID",
        paid_remark: paidRemark.trim(),
      });
      setMarkPaidTip(null);
      setPaidRemark("");
      fetchTips();
    } catch (e) {
      console.error(e);
    } finally {
      setPaidLoading(false);
    }
  };

  return (
    <Box sx={{ width: "100%", marginTop: { xs: "55px", md: "95px" } }}>
      <Box p={3} sx={{ padding: { xs: "16px", md: "32px" } }}>

        {/* HEADER */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" fontWeight="bold">Tip Management</Typography>
            <Typography color="text.secondary">Track employee wise tips</Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDrawer(true)}
              sx={{ borderRadius: 3, height: 45 }}
            >
              Create Tip
            </Button>
          </Box>
        </Box>

        {/* FILTERS */}
        <Box display="flex" gap={2} alignItems="center" mb={3} flexWrap="wrap">
          <TextField
            size="small" type="date" label="From"
            InputLabelProps={{ shrink: true }}
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
          />
          <TextField
            size="small" type="date" label="To"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
          />
          <TextField
            size="small" label="Employee Name" placeholder="Search employee..."
            value={empFilter}
            onChange={(e) => { setEmpFilter(e.target.value); setPage(0); }}
            sx={{ minWidth: 200 }}
          />
          {(dateFrom || dateTo || empFilter) && (
            <Button
              size="small" variant="outlined" color="inherit"
              onClick={() => { setDateFrom(""); setDateTo(""); setEmpFilter(""); setPage(0); }}
            >
              Clear
            </Button>
          )}
        </Box>

        {/* SUMMARY BANNER */}
        {!isCashier && (
          <Paper sx={{
            p: 2, mb: 3, borderRadius: 2,
            background: "linear-gradient(135deg,#1e3c72,#2a5298)",
            color: "#fff",
            display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2,
          }}>
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Total Tips</Typography>
              <Typography variant="h6" fontWeight={700}>{pagination?.totalRecords || 0} records</Typography>
            </Box>
            <Box textAlign="right">
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Total Tip Amount</Typography>
              <Typography variant="h4" fontWeight={800}>{Number(totalTipAmount).toLocaleString("en-IN")}</Typography>
            </Box>
          </Paper>
        )}

        {/* TABLE */}
        <Paper sx={{ borderRadius: 4, overflow: "hidden" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Employee Name</TableCell>
                <TableCell>Designation</TableCell>
                <TableCell>Chips In</TableCell>
                <TableCell>Tip Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tips.length > 0 ? (
                tips.map((tip) => (
                  <TableRow key={tip._id}>
                    <TableCell>{new Date(tip.date).toLocaleString()}</TableCell>
                    <TableCell>{tip.employee_name || "—"}</TableCell>
                    <TableCell>{tip.employee_designation || "—"}</TableCell>
                    <TableCell>{tip.total_in?.toLocaleString() || 0}</TableCell>
                    <TableCell>
                      <Typography fontWeight={700} color="success.main">
                        {tip.rake_amount?.toLocaleString() || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tip.status}
                        color={
                          tip.status === "PAID"    ? "success" :
                          tip.status === "OPEN"    ? "warning" :
                          tip.status === "RUNNING" ? "info"    :
                          tip.status === "CLOSED"  ? "default" : "default"
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={1} justifyContent="center">
                        <IconButton
                          size="small" color="primary"
                          onClick={() => { setSelectedTip(tip); setOpenView(true); }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        {tip.status !== "PAID" && (
                          <Button
                            size="small" variant="outlined" color="success"
                            onClick={() => { setPaidRemark(""); setMarkPaidTip(tip); }}
                            sx={{ fontSize: 11, px: 1.2, py: 0.3, whiteSpace: "nowrap" }}
                          >
                            Mark Paid
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">No Tips Found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={pagination.totalRecords || 0}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[20]}
          />
        </Paper>

        <CreateTipDrawer open={openDrawer} onClose={() => setOpenDrawer(false)} refresh={fetchTips} />
        <TipViewDialog  open={openView}   tip={selectedTip} onClose={() => setOpenView(false)} refresh={fetchTips} />

        {/* MARK PAID DIALOG */}
        <Dialog open={!!markPaidTip} onClose={() => setMarkPaidTip(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          <Box sx={{ background: "linear-gradient(135deg,#1e3c72,#2a5298)", px: 3, py: 2, color: "#fff" }}>
            <Typography variant="h6" fontWeight={700}>Mark Tip as Paid</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {markPaidTip?.employee_name} — {markPaidTip?.rake_amount?.toLocaleString()}
            </Typography>
          </Box>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              fullWidth size="small"
              label="Remark (required)"
              placeholder="e.g. Paid via bank transfer"
              value={paidRemark}
              onChange={(e) => setPaidRemark(e.target.value)}
              multiline rows={2}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button variant="outlined" onClick={() => setMarkPaidTip(null)} disabled={paidLoading}>Cancel</Button>
            <Button
              variant="contained" color="success"
              onClick={handleMarkPaidConfirm}
              disabled={paidLoading || !paidRemark.trim()}
            >
              {paidLoading ? "Saving…" : "Confirm Paid"}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Box>
  );
}
